import jsPDF from "jspdf";
import autoTable from "jspdf-autotable";

interface TestReportData {
  orderId: string;
  projectName: string;
  clientName: string;
  docRefNo: string;
  date: string;
  cementType: string;
  cubeSize: string;
  gradeOfConcrete: string;
  tests: Array<{
    srNo: number;
    location: string;
    slumpMm: string;
    cubeIdMark: string;
    dateOfCasting: string;
    dateOfTesting: string;
    timeOfTesting: string;
    ageOfCube: string;
    weightOfCube: string;
    crushingLoad: string;
    compressiveStrength: string;
    avgCompressiveStrength?: string;
  }>;
  vehicleDetails?: {
    vehicleNumber: string;
    driverName: string;
    departureTime: string;
  };
  remarks: string;
  signatures: {
    engineerQAQC: string;
    managerQAQC: string;
    projectManager: string;
  };
}

export function generateProfessionalPDF(data: TestReportData): void {
  const doc = new jsPDF();
  const pageWidth = doc.internal.pageSize.width;
  const pageHeight = doc.internal.pageSize.height;

  // Colors
  const primaryColor = "#1B2A5B";
  const borderColor = "#000000";
  const lightGray = "#F9FAFB";

  // Header Section with Logo placeholders and Title
  doc.setDrawColor(0, 0, 0);
  doc.setLineWidth(0.5);

  // Logo boxes (left and right)
  doc.rect(10, 10, 30, 20);
  doc.setFontSize(8);
  doc.setFont("helvetica", "normal");
  doc.text("COMPANY", 25, 18, { align: "center" });
  doc.text("LOGO", 25, 23, { align: "center" });

  doc.rect(pageWidth - 40, 10, 30, 20);
  doc.text("PROJECT", pageWidth - 25, 18, { align: "center" });
  doc.text("LOGO", pageWidth - 25, 23, { align: "center" });

  // Title in center
  doc.setFontSize(16);
  doc.setFont("helvetica", "bold");
  doc.text("CONCRETE CUBE TEST REPORT", pageWidth / 2, 20, {
    align: "center",
  });

  // Project Information Section
  let yPos = 35;
  doc.setLineWidth(0.3);

  // Project info table
  const projectInfo = [
    ["Name of Project:", data.projectName || "N/A"],
    ["Name of Client:", data.clientName || "N/A"],
    ["Doc Ref no:", data.docRefNo || "N/A"],
    ["Date:", data.date || "N/A"],
  ];

  projectInfo.forEach((row) => {
    doc.rect(10, yPos, 90, 8);
    doc.rect(100, yPos, 100, 8);
    doc.setFontSize(10);
    doc.setFont("helvetica", "normal");
    doc.text(String(row[0]), 12, yPos + 5);
    doc.text(String(row[1]), 102, yPos + 5);
    yPos += 8;
  });

  // Technical specifications
  const techSpecs = [
    ["Cement:", data.cementType || "N/A"],
    ["Cube size:", data.cubeSize || "N/A"],
    ["Grade of Concrete:", data.gradeOfConcrete || "N/A"],
  ];

  techSpecs.forEach((row) => {
    doc.rect(10, yPos, 90, 8);
    doc.rect(100, yPos, 100, 8);
    doc.setFontSize(10);
    doc.setFont("helvetica", "normal");
    doc.text(String(row[0]), 12, yPos + 5);
    doc.text(String(row[1]), 102, yPos + 5);
    yPos += 8;
  });

  yPos += 5;

  // Vehicle Details Section (if available)
  if (data.vehicleDetails && data.vehicleDetails.vehicleNumber) {
    doc.setFontSize(11);
    doc.setFont("helvetica", "bold");
    doc.text("Vehicle Details:", 10, yPos);
    yPos += 7;

    doc.setFontSize(9);
    doc.setFont("helvetica", "normal");
    doc.text(
      `Vehicle No: ${data.vehicleDetails.vehicleNumber} | Driver: ${data.vehicleDetails.driverName} | Departure: ${data.vehicleDetails.departureTime}`,
      10,
      yPos
    );
    yPos += 10;
  }

  // Main Test Data Table
  const tableHeaders = [
    [
      "Sr. No",
      "Location",
      "Slump (mm)",
      "Cube Id mark",
      "Date of Casting",
      "Date of Testing",
      "Time of Testing",
      "Age of Cube",
      "Weight of Cube (Grams)",
      "Crushing Load (KN)",
      "Compressive Strength (N/mm²)",
      "Avg Compressive Strength (N/mm²)",
    ],
  ];

  const tableData = data.tests.map((test) => [
    test.srNo.toString(),
    test.location,
    test.slumpMm,
    test.cubeIdMark,
    test.dateOfCasting,
    test.dateOfTesting,
    test.timeOfTesting,
    test.ageOfCube,
    test.weightOfCube,
    test.crushingLoad,
    test.compressiveStrength,
    test.avgCompressiveStrength || "",
  ]);

  autoTable(doc, {
    startY: yPos,
    head: tableHeaders,
    body: tableData,
    theme: "grid",
    styles: {
      fontSize: 7,
      cellPadding: 2,
      lineColor: [0, 0, 0],
      lineWidth: 0.3,
    },
    headStyles: {
      fillColor: [255, 255, 255],
      textColor: [0, 0, 0],
      fontStyle: "bold",
      halign: "center",
      valign: "middle",
      fontSize: 7,
    },
    bodyStyles: {
      textColor: [0, 0, 0],
      halign: "center",
    },
    columnStyles: {
      0: { cellWidth: 8 },
      1: { cellWidth: 18 },
      2: { cellWidth: 12 },
      3: { cellWidth: 14 },
      4: { cellWidth: 16 },
      5: { cellWidth: 16 },
      6: { cellWidth: 14 },
      7: { cellWidth: 13 },
      8: { cellWidth: 16 },
      9: { cellWidth: 14 },
      10: { cellWidth: 18 },
      11: { cellWidth: 21 },
    },
  });

  // Get final Y position after table
  yPos = (doc as any).lastAutoTable.finalY + 10;

  // Remarks Section
  doc.setFontSize(10);
  doc.setFont("helvetica", "bold");
  doc.text("Remarks:", 10, yPos);
  yPos += 7;

  doc.rect(10, yPos, pageWidth - 20, 20);
  doc.setFontSize(9);
  doc.setFont("helvetica", "normal");

  if (data.remarks) {
    const remarksLines = doc.splitTextToSize(data.remarks, pageWidth - 25);
    doc.text(remarksLines, 12, yPos + 5);
  }

  yPos += 25;

  // Signature Section
  const sigWidth = (pageWidth - 30) / 3;

  doc.rect(10, yPos, sigWidth, 25);
  doc.rect(10 + sigWidth, yPos, sigWidth, 25);
  doc.rect(10 + sigWidth * 2, yPos, sigWidth, 25);

  doc.setFontSize(9);
  doc.setFont("helvetica", "normal");

  // Engineer QA/QC
  doc.text("Signature of Engineer QA/QC", 10 + sigWidth / 2, yPos + 5, {
    align: "center",
  });
  if (data.signatures.engineerQAQC) {
    doc.setFont("helvetica", "italic");
    doc.text(data.signatures.engineerQAQC, 10 + sigWidth / 2, yPos + 15, {
      align: "center",
    });
  }

  // Manager QA/QC
  doc.setFont("helvetica", "normal");
  doc.text(
    "Signature of Manager QA/QC",
    10 + sigWidth + sigWidth / 2,
    yPos + 5,
    { align: "center" }
  );
  if (data.signatures.managerQAQC) {
    doc.setFont("helvetica", "italic");
    doc.text(
      data.signatures.managerQAQC,
      10 + sigWidth + sigWidth / 2,
      yPos + 15,
      { align: "center" }
    );
  }

  // Project Manager
  doc.setFont("helvetica", "normal");
  doc.text(
    "Signature of Project Manager",
    10 + sigWidth * 2 + sigWidth / 2,
    yPos + 5,
    { align: "center" }
  );
  if (data.signatures.projectManager) {
    doc.setFont("helvetica", "italic");
    doc.text(
      data.signatures.projectManager,
      10 + sigWidth * 2 + sigWidth / 2,
      yPos + 15,
      { align: "center" }
    );
  }

  // Footer
  yPos = pageHeight - 15;
  doc.setFontSize(8);
  doc.setFont("helvetica", "normal");
  doc.setTextColor(100, 100, 100);
  doc.text(
    `Generated on ${new Date().toLocaleDateString()} | Document: ${
      data.docRefNo
    }`,
    pageWidth / 2,
    yPos,
    { align: "center" }
  );

  // Save the PDF
  doc.save(
    `Concrete_Test_Report_${data.orderId}_${new Date().getTime()}.pdf`
  );
}